<script lang="ts">
	import { featuredMediaUrl } from "@fergmar/ui/media";
	import { Badge } from "#lib/web-ui/ui/badge/index.js";
	import BedDoubleIcon from "@lucide/svelte/icons/bed-double";
	import BathIcon from "@lucide/svelte/icons/bath";
	import TreesIcon from "@lucide/svelte/icons/trees";
	import HomeIcon from "@lucide/svelte/icons/home";
	import { formatCurrencyRound } from "@fergmar/ui/formatters";
	import { cloudinaryFetch } from "#lib/shared/cloudinary.js";
	import type { PublicInvestmentListing } from "@fergmar/contracts/public/investment-listings";

	type PublicListing = PublicInvestmentListing;

	let { listing }: { listing: PublicListing } = $props();

	const imageUrl = $derived(
		cloudinaryFetch(featuredMediaUrl(listing.media, "cardUrl"), {
			width: 400,
			height: 300,
			crop: "fill"
		})
	);

	let imageError = $state(false);

	function handleImageError() {
		imageError = true;
	}
</script>

<a
	href="./for-sale/{listing.slug}"
	class="hover:no-underline"
	data-sveltekit-preload-data="tap"
	data-sveltekit-preload-code="hover"
>
	<div class="flex flex-col rounded border">
		<div class="relative">
			<Badge class="absolute top-2 right-2 rounded">
				{listing.propertyType}
			</Badge>
		</div>
		<div class="h-48 w-full overflow-hidden rounded-t shadow-xl">
			{#if imageUrl && !imageError}
				<img
					src={imageUrl}
					alt={`${listing.addressStreet1} ${listing.addressCity} ${listing.addressState} ${listing.addressZip} Featured Image`}
					class="h-full w-full object-cover object-center"
					loading="lazy"
					decoding="async"
					width="400"
					height="300"
					onerror={handleImageError}
				/>
			{:else}
				<!-- Gradient background placeholder with property info -->
				<div
					class="flex h-full w-full items-center justify-center bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500"
				>
					<div class="text-center text-white">
						<HomeIcon class="mx-auto mb-3 h-16 w-16 opacity-90" />
						<p class="mb-1 text-lg font-semibold">{listing.propertyType}</p>
						<p class="text-sm opacity-90">
							{listing.addressCity}, {listing.addressState}
						</p>
					</div>
				</div>
			{/if}
		</div>
		<div class="p-4">
			<!-- Price -->
			<div class="mb-2 flex items-start justify-between">
				<span class="text-lg font-bold">
					{#if listing.currentPrice}
						{formatCurrencyRound(listing.currentPrice)}
					{:else}
						Contact for Price
					{/if}
				</span>
			</div>

			<!-- Address Info -->
			<div class="flex flex-col items-start">
				<span class="mb-1 font-semibold">{listing.addressStreet1}</span>
				<span class="text-foreground/70 text-sm"
					>{listing.addressCity}, {listing.addressState}
					{listing.addressZip}</span
				>
			</div>

			<hr class="bg-border my-3" />

			<!-- Building Details -->
			<div class="text-muted-foreground mt-2 flex min-h-[20px] flex-row gap-3 text-sm">
				{#if listing.bedrooms || listing.totalBathrooms || listing.livingArea || listing.lotSize}
					{#if listing.bedrooms}
						<div class="flex flex-row items-center gap-1">
							<BedDoubleIcon class="h-4 w-4" />
							<span>{`${listing.bedrooms} Beds`}</span>
						</div>
					{/if}
					{#if listing.totalBathrooms}
						<div class="flex flex-row items-center gap-1">
							<BathIcon class="h-4 w-4" />
							<span>{`${listing.totalBathrooms} Baths`}</span>
						</div>
					{/if}
					{#if listing.livingArea || listing.lotSize}
						{#if listing.livingArea}
							<div class="flex flex-row items-center gap-1">
								<HomeIcon class="h-4 w-4" />
								<span>{`${listing.livingArea.toLocaleString()} sqft`}</span>
							</div>
						{:else}
							<div class="flex flex-row items-center gap-1">
								<TreesIcon class="h-4 w-4" />
								<span>
									{#if listing.lotSizeUnit === "Acres"}
										{`${listing.lotSize} Acres`}
									{:else}
										{`${(listing.lotSize / 43560).toFixed(2)} Acre(s)`}
									{/if}
								</span>
							</div>
						{/if}
					{/if}
				{:else}
					<span>Call for details</span>
				{/if}
			</div>
		</div>
	</div>
</a>
